<template>
  <v-card v-if="options">
    <v-card-title v-if="fund.name">
      {{ fund.name }}

      <v-spacer></v-spacer>

      <v-chip v-if="fund.state === 'DONE'" color="green" dark small outlined>
        {{ $t("user.fundCard.done") }}
      </v-chip>
    </v-card-title>

    <v-card-text v-if="precheckFirst">
      {{ $t("user.fundCard.precheckFirst.description") }}
    </v-card-text>

    <v-simple-table class="table-no-hover" v-if="!precheckFirst">
      <template #default>
        <tbody>
          <tr v-if="isYellow">
            <td width="50%" colspan="3">
              <v-alert color="yellow">
                {{ $t("user.fundCard.yellow.description") }}
              </v-alert>
            </td>
          </tr>

          <tr v-if="isOrange">
            <td width="50%" colspan="3">
              <v-alert color="orange" dark>
                {{ $t("user.fundCard.orange.description") }}
              </v-alert>
            </td>
          </tr>

          <tr v-if="fund.precheck">
            <td width="50%" colspan="3">
              <strong>
                {{ $t("user.fundCard.precheck.label") }}
              </strong>
            </td>
          </tr>

          <tr v-if="fund.state === 'NEW'">
            <td width="50%">
              {{ $t("user.fundCard.edit.label") }}
            </td>

            <td></td>

            <td width="1%">
              <v-btn v-if="mayStartEdit" :to="{ name: 'userFundBase', params: { id: fund.id } }" color="secondary" icon>
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="fund.state !== 'NEW' && isEditable">
            <td width="50%">
              {{ $t("user.fundCard.edit.label") }}
            </td>

            <td></td>

            <td width="1%">
              <v-btn :to="{ name: 'userFundEdit', params: { id: fund.id } }" color="secondary" icon>
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="fund.state !== 'DONE' && !auditPeriodIsClosed">
            <td>
              {{ fund.precheck ? $t("user.fundCard.precheck.label") : $t("user.fundCard.minimal.label") }}
            </td>

            <td>
              {{ minimalRequirementsText }}
            </td>

            <td>
              <v-btn
                v-if="mayEditMinimalRequirements"
                :to="{ name: 'userFundMinimal', params: { id: fund.id } }"
                color="secondary"
                icon
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="fund.state !== 'DONE' && fund.stepModel && !auditPeriodIsClosed">
            <td>
              {{ $t("user.fundCard.stepModel.label") }}
            </td>

            <td>
              {{ stepModelRequirementsText }}
            </td>

            <td>
              <v-btn v-if="auditPeriod.mayChooseInterview" href="#choose-interview" color="secondary" icon>
                <v-icon>mdi-chevron-triple-up</v-icon>
              </v-btn>

              <v-btn
                v-if="mayEditStepModelRequirements"
                :to="{ name: 'userFundStep', params: { id: fund.id } }"
                color="secondary"
                icon
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="fund.state !== 'NEW'">
            <td>
              {{ $t("user.fundCard.protocol.label") }}
            </td>

            <td></td>

            <td width="1%">
              <v-btn :to="{ name: 'userFundProtocol', params: { id: fund.id } }" color="secondary" icon>
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="userMaySeeProvisionalReport">
            <td>
              {{ $t("user.fundCard.provisionalReport") }}
            </td>

            <td></td>

            <td>
              <v-btn :to="{ name: 'userProvisionalReport', params: { id: fund.id } }" color="secondary" icon>
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </td>
          </tr>

          <tr v-if="userMaySeeFinalReport">
            <td>
              {{ $t("user.fundCard.finalReport") }}
            </td>

            <td></td>

            <td>
              <v-btn :to="{ name: 'userFinalReport', params: { id: fund.id } }" color="secondary" icon>
                <v-icon>mdi-eye</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </v-card>
</template>

<script>
import fundIsEditable from "@fng/common/fundIsEditable";
import userMaySeeFinalReport from "@fng/common/userMaySeeFinalReport";
import mayEditMinimalRequirements from "@fng/common/mayEditMinimalRequirements";
import userMaySeeProvisionalReport from "@fng/common/userMaySeeProvisionalReport";
import mayEditStepModelRequirements from "@fng/common/mayEditStepModelRequirements";
import OptionsQuery from "@/gql/queries/auditor/OptionsQuery.gql";
import PrecheckFirstQuery from "@/gql/queries/PrecheckFirstQuery.gql";
import auditPeriodIsClosed from "@fng/common/auditPeriodIsClosed";

export default {
  props: {
    fund: {
      type: Object,
      required: true,
    },

    auditPeriod: {
      type: Object,
      required: true,
    },

    mayStartEdit: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      options: null,
      currentUser: null,
    };
  },

  apollo: {
    options: {
      query: OptionsQuery,
      fetchPolicy: "cache-and-network",
    },

    currentUser: {
      query: PrecheckFirstQuery,
      fetchPolicy: "cache-and-network",
    },
  },

  computed: {
    isEditable() {
      return this.mayStartEdit && fundIsEditable(this.fund, this.auditPeriod);
    },

    mayEditMinimalRequirements() {
      return this.mayStartEdit && mayEditMinimalRequirements(this.fund, this.auditPeriod);
    },

    minimalRequirementsText() {
      if (auditPeriodIsClosed(this.auditPeriod)) return this.$t("user.fundCard.requirementState.closed");

      if (this.fund.state === "NEW") return this.$t("user.fundCard.requirementState.new");

      if (this.fund.minimalRequirementsState === "AUDIT") return this.$t("user.fundCard.requirementsState.audit");

      if (this.fund.minimalRequirementsState === "FINAL_AUDIT")
        return this.$t("user.fundCard.requirementsState.finalAudit");

      if (this.fund.minimalRequirementsState === "DONE") return this.$t("user.fundCard.requirementsState.done");

      return this.$t("user.fundCard.requirementsState.edit");
    },

    mayEditStepModelRequirements() {
      return this.mayStartEdit && mayEditStepModelRequirements(this.fund, this.auditPeriod);
    },

    stepModelRequirementsText() {
      if (auditPeriodIsClosed(this.auditPeriod)) return this.$t("user.fundCard.requirementState.closed");

      if (this.fund.state === "NEW") return this.$t("user.fundCard.requirementState.new");

      if (this.auditPeriod.mayChooseInterview) return this.$t("user.fundCard.requirementState.chooseInterview");

      if (this.fund.stepModelRequirementsState === "AUDIT") return this.$t("user.fundCard.requirementsState.audit");

      if (this.fund.stepModelRequirementsState === "SECOND_AUDIT")
        return this.$t("user.fundCard.requirementsState.audit");

      if (this.fund.stepModelRequirementsState === "FINAL_AUDIT")
        return this.$t("user.fundCard.requirementsState.finalAudit");

      if (this.fund.stepModelRequirementsState === "INTERVIEW")
        return this.$t("user.fundCard.requirementsState.interview");

      if (this.fund.stepModelRequirementsState === "INTERVIEW_AUDIT")
        return this.$t("user.fundCard.requirementsState.audit");

      if (this.fund.stepModelRequirementsState === "DONE") return this.$t("user.fundCard.requirementsState.done");

      return this.$t("user.fundCard.requirementsState.edit");
    },

    userMaySeeProvisionalReport() {
      return userMaySeeProvisionalReport(this.fund);
    },

    userMaySeeFinalReport() {
      return userMaySeeFinalReport(this.fund);
    },

    yellowDeadline() {
      const yellowDeadline = this.options?.find(
        (option) => option.key === (this.fund.precheck ? "precheckYellowDeadline" : "yellowDeadline")
      )?.value;

      return new Date(yellowDeadline);
    },

    isYellow() {
      if (this.fund.state === "DONE") return false;
      if (this.auditPeriod.year !== new Date().getFullYear()) return false;
      if (this.isOrange) return false;

      const initialEdit = this.fund.stepModel ? this.fund.initialStepModelEditAt : this.fund.initialMinimalEditAt;

      if (!initialEdit) {
        return new Date() > this.yellowDeadline;
      }

      const initialEditDate = new Date(initialEdit);

      return this.yellowDeadline && this.yellowDeadline < initialEditDate;
    },

    orangeDeadline() {
      const orangeDeadline = this.options?.find(
        (option) => option.key === (this.fund.precheck ? "precheckOrangeDeadline" : "orangeDeadline")
      )?.value;

      return new Date(orangeDeadline);
    },

    isOrange() {
      if (this.fund.state === "DONE") return false;
      if (this.auditPeriod.year !== new Date().getFullYear()) return false;

      const initialEdit = this.fund.stepModel ? this.fund.initialStepModelEditAt : this.fund.initialMinimalEditAt;

      if (!initialEdit) {
        return new Date() > this.orangeDeadline;
      }

      const initialEditDate = new Date(initialEdit);

      return this.orangeDeadline && this.orangeDeadline < initialEditDate;
    },

    precheckFirst() {
      if (!this.currentUser?.precheckFirst) return false;
      if (this.fund.precheck) return false;
      if (!this.auditPeriod?.funds) return false;

      return this.auditPeriod.funds.some((fund) => fund.precheck && fund.state !== "DONE");
    },

    auditPeriodIsClosed() {
      return auditPeriodIsClosed(this.auditPeriod);
    },
  },
};
</script>
